/**
 * Form Component
 * The new form component is now responsible for handling form accessibility as well as form status indicator
 *
 */
import { Component, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { NgForm, FormsModule } from '@angular/forms';

import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { IdService } from '../../services/id.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { FormAccessibilityService } from './accessibility-alert/form-accessibility.service';
import { FormAccessibilityAlertComponent } from './accessibility-alert/form-accessibility-alert.component';
import { FORM_HOST, type FormRowRef } from './form.contract';
import {
  FORM_SHELL,
  type FormAccessibilityScope,
  type FormShell,
  type FormShellStatus
} from './form-shell.contract';

import type {
  AccessibleErrors,
  FormRowStatus,
  FormType,
  FormValidityStatus,
  InputValidationError
} from './domain/form.domain';

import type { AbstractControl, NgModel } from '@angular/forms';
import type { AfterContentInit, OnDestroy, OnInit, Signal } from '@angular/core';

type SubmitTrackingFormControl = AbstractControl & { submitted: boolean };

@Component({
  selector: 'td-wb-form',
  templateUrl: 'form.component.html',
  styleUrls: ['form.component.scss'],
  exportAs: 'FormComponent',
  imports: [FormsModule, FormAccessibilityAlertComponent,
    TranslatePipe],
  providers: [
    { provide: FORM_HOST, useExisting: FormComponent },
    { provide: FORM_SHELL, useExisting: FormComponent }
  ],
  host: {
    '[class.td-wb-form--narrative]': 'isNarrative'
  }
})
export class FormComponent implements OnInit, OnDestroy, AfterContentInit, FormShell {
  private static readonly HEADER_ID_PREFIX: string = 'td-wb-errors-aria-header';
  readonly proceedOnError = input<boolean>(false);
  readonly formType = input<FormType>('default');
  readonly accessibilityScope = input<FormAccessibilityScope>('auto');

  readonly rowStatusIndicator = input(false);

  readonly statusChanges = output<FormValidityStatus>();
  readonly rowStatus = output<FormRowStatus>();
  readonly submitAction = output<NgForm>();
  readonly formElement = viewChild(NgForm);
  private readonly statusVersion = signal(0);

  readonly shellStatus: Signal<FormShellStatus> = computed((): FormShellStatus => {
    this.statusVersion();
    const formElement = this.formElement();
    return {
      pristine: formElement?.pristine ?? true,
      valid: formElement?.valid ?? false,
      invalid: formElement?.invalid ?? false,
      pending: formElement?.pending ?? false,
      submissionAttempted: this.formSubmitted,
      submitting: false
    };
  });

  isNarrative: boolean = false;

  accessibleHeaderId: string;
  accessibleErrors: AccessibleErrors = {};

  displayedAccessibleErrors: Array<string>;

  readonly accessibleErrorsSubject: BehaviorSubject<Array<string>> = new BehaviorSubject<Array<string>>([]);

  private formSubmitted: boolean = false;
  private readonly displayedErrorsSubject: BehaviorSubject<AccessibleErrors> = new BehaviorSubject<AccessibleErrors>({});
  private rows: Array<FormRowRef> = [];
  private rowId: number = 0;
  private focusedRowId: number = -1;
  private readonly destroy: Subject<void> = new Subject<void>();
  private isModalForm: boolean = false;

  private readonly idService = inject(IdService);
  private readonly formAccessibilityService = inject(FormAccessibilityService);

  ngOnInit(): void {
    // create a unique id for the form for accessibility purposes
    this.accessibleHeaderId = this.idService.generateNamespaceSequentialId(FormComponent.HEADER_ID_PREFIX);
    this.isNarrative = this.formType() === 'narrative';
    this.isModalForm = this.accessibilityScope() === 'auto'
      ? this.formAccessibilityService.isModal()
      : this.accessibilityScope() === 'modal';
    this.formAccessibilityService.registerErrors(this.displayedErrorsSubject, this.isModalForm);
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.displayedErrorsSubject.next({});
    this.displayedErrorsSubject.complete();
    this.formAccessibilityService.unregisterErrors(this.isModalForm, this.displayedErrorsSubject);
  }

  ngAfterContentInit(): void {
    this.formElement().statusChanges
      .pipe(takeUntil(this.destroy))
      .subscribe((status: FormValidityStatus): void => {
        this.statusVersion.update((version: number): number => version + 1);
        this.statusChanges.emit(status);
      });
  }

  get pristine(): boolean {
    const formElement = this.formElement();
    return formElement !== null && formElement.pristine;
  }

  get valid(): boolean {
    const formElement = this.formElement();
    return formElement !== null && formElement.valid;
  }

  get invalid(): boolean {
    const formElement = this.formElement();
    return formElement !== null && formElement.invalid;
  }

  get submitted(): boolean {
    return this.formSubmitted;
  }

  setAccessibleError(inputName: string, error?: InputValidationError): void {
    const key = FormAccessibilityService.getErrorKey(inputName);
    if (error !== undefined) {
      this.accessibleErrors[key] = error;
    } else {
      delete this.accessibleErrors[key];
    }
    this.onErrorCountChange();
  }

  hasErrors(): boolean {
    return this.getVisibleErrorCount() > 0;
  }

  getVisibleErrorCount(): number {
    return this.formAccessibilityService.getVisibleErrorCount(this.isModalForm);
  }

  onErrorCountChange(): void {
    this.displayedAccessibleErrors = Object.keys(this.accessibleErrors)
      .filter((error: string): boolean => this.accessibleErrors[error].isDisplayed);
    this.accessibleErrorsSubject.next(this.displayedAccessibleErrors);
    this.displayedErrorsSubject.next(this.accessibleErrors);
  }

  addRow(row: FormRowRef): FormRowRef {
    row.id = this.rowId++;
    this.rows.push(row);
    return row;
  }

  removeRow(row: FormRowRef): void {
    const rowIndex = this.rows.findIndex((existingRow: FormRowRef) => existingRow?.id === row.id);
    if (rowIndex !== -1) {
      if (this.focusedRowId === row.id) {
        this.focusedRowId = -1;
      }
      this.rows.splice(rowIndex, 1);
    }
  }

  isFormSubmitted(): boolean {
    return this.formSubmitted;
  }

  submit(): boolean {
    return this.maybeSubmitForm();
  }

  requestSubmit(): void {
    this.submit();
  }

  reset(value?: unknown): void {
    this.resetForm(value);
    this.formSubmitted = false;
    this.statusVersion.update((version: number): number => version + 1);
  }

  // deprecated - use submit
  maybeSubmitForm(): boolean {
    const formElement = this.formElement();
    if (formElement.invalid) {
      // (this.formElement as { submitted: boolean }).submitted = true; // NOTE: work around readonly flag
      this.formSubmitted = true;
      this.statusVersion.update((version: number): number => version + 1);
      Object.keys(formElement.form.controls).forEach((control: string): void => {
        const formControl = this.formElement().form.get(control);
        this.markAsSubmitted(formControl as SubmitTrackingFormControl);
        formControl.markAsDirty();
        formControl.markAsTouched();
        formControl.updateValueAndValidity({
          emitEvent: true
        });
        this.formAccessibilityService.scrollErrorIntoView(this.isModalForm);
      });
    }
    if (formElement.valid || this.proceedOnError()) {
      this.submitAction.emit(formElement);
    }
    return formElement.valid;
  }

  resetForm(value?: unknown): void {
    this.formElement().resetForm(value);
    if (this.formSubmitted) {
      Object.keys(this.formElement().form.controls).forEach((control: string): void => {
        const formControl = this.formElement().form.get(control);
        this.markAsUnsubmitted(formControl as SubmitTrackingFormControl);
      });
    }
  }

  setDirty(): void {
    this.formElement().form.markAsDirty();
    this.formElement().form.markAsTouched();
    this.statusVersion.update((version: number): number => version + 1);
  }

  setPristine(): void {
    this.formElement().form.markAsPristine();
    this.formElement().form.markAsUntouched();
    this.statusVersion.update((version: number): number => version + 1);
  }

  focusFirstError(): void {
    this.formAccessibilityService.scrollErrorIntoView(this.isModalForm);
  }

  registerFormControl(inputModel: NgModel): void {
    this.formElement().addControl(inputModel);
  }

  unRegisterFormControl(inputModel: NgModel): void {
    this.formElement().removeControl(inputModel);
  }

  setRowStatus(rowStatus: FormRowStatus): void {
    this.rowStatus.emit(rowStatus);
  }

  private markAsSubmitted(formControl: SubmitTrackingFormControl): void {
    formControl.submitted = true;
  }

  private markAsUnsubmitted(formControl: SubmitTrackingFormControl): void {
    delete formControl.submitted;
  }
}
